/**
 * Every change anyone can make to a floor, as pure functions.
 *
 * The assistant routes a sentence to one of these and the dialog's buttons
 * call the same ones, so a floor built by talking and a floor built by
 * clicking go through identical code. That is not tidiness - it is what
 * makes the assistant's reply trustworthy, because the reply is written from
 * what the edit ACTUALLY did (`Change` below) rather than from what the
 * sentence asked for. Ask for a tool that is already there and the answer
 * says so, because `added` came back empty.
 *
 * Nothing here touches storage or the hall. A plan is data until somebody
 * saves it.
 */

import { REQUIRED_TOOL_IDS } from '../../fab/layout'
import type { FloorPlan, PlanHall } from '../../fab/floorPlan'
import { archetypeTools } from './archetypes'
import { flowIndex, idsInZone, inFlowOrder, station, type FlowZone } from './catalogue'

/** What an edit did, which is not always what it was asked to do. */
export interface Change {
  plan: FloorPlan
  added: string[]
  removed: string[]
  /** Asked for but already on the floor, or asked to remove and not there. */
  unchanged: string[]
  /** Asked to remove, and refused because the floor needs it. */
  refused: string[]
  renamed: boolean
  hallChanged: boolean
  reordered: boolean
}

const none = (plan: FloorPlan): Change => ({
  plan,
  added: [],
  removed: [],
  unchanged: [],
  refused: [],
  renamed: false,
  hallChanged: false,
  reordered: false,
})

/** True when the edit left the floor exactly as it found it. */
export const didNothing = (c: Change): boolean =>
  !c.added.length && !c.removed.length && !c.renamed && !c.hallChanged && !c.reordered

const REQUIRED = new Set(REQUIRED_TOOL_IDS)

/**
 * Put a tool where the flow says it goes.
 *
 * Only among the tools it is being added NEXT TO: a floor whose author has
 * deliberately put metrology last stays that way, and the new tool lands at
 * the first position where everything before it comes earlier in the flow.
 * A plan in flow order therefore stays in flow order, and one that is not is
 * not silently re-sorted behind its author's back.
 */
function insertByFlow(tools: string[], id: string): string[] {
  const at = flowIndex(id)
  let i = 0
  while (i < tools.length && flowIndex(tools[i]) <= at) i++
  return [...tools.slice(0, i), id, ...tools.slice(i)]
}

export function addTools(plan: FloorPlan, ids: string[]): Change {
  const c = none(plan)
  let tools = plan.tools
  for (const id of ids) {
    if (!station(id)) continue
    if (tools.includes(id)) {
      c.unchanged.push(id)
      continue
    }
    tools = insertByFlow(tools, id)
    c.added.push(id)
  }
  c.plan = { ...plan, tools }
  return c
}

export function removeTools(plan: FloorPlan, ids: string[]): Change {
  const c = none(plan)
  const drop = new Set<string>()
  for (const id of ids) {
    if (!station(id)) continue
    if (REQUIRED.has(id)) {
      c.refused.push(id)
      continue
    }
    if (!plan.tools.includes(id)) {
      c.unchanged.push(id)
      continue
    }
    drop.add(id)
    c.removed.push(id)
  }
  c.plan = { ...plan, tools: plan.tools.filter((t) => !drop.has(t)) }
  return c
}

/** Everything in one stage of the flow, added or taken away together. */
export const addZone = (plan: FloorPlan, zone: FlowZone): Change =>
  addTools(plan, idsInZone(zone))

export const removeZone = (plan: FloorPlan, zone: FlowZone): Change =>
  removeTools(plan, idsInZone(zone))

/**
 * Start again from a fab that exists.
 *
 * A replacement rather than a merge: picking "assembly and test house" after
 * building a front end means that, not a front end with a test floor bolted
 * on. The diff is still reported honestly, so the reply can say what left.
 */
export function useArchetype(plan: FloorPlan, id: string): Change {
  const tools = archetypeTools(id)
  if (!tools.length) return none(plan)
  const c = none(plan)
  const before = new Set(plan.tools)
  const after = new Set(tools)
  c.added = tools.filter((t) => !before.has(t))
  c.removed = plan.tools.filter((t) => !after.has(t) && !REQUIRED.has(t))
  c.plan = { ...plan, tools: inFlowOrder([...REQUIRED_TOOL_IDS, ...tools]) }
  // The same stations in a different order is still a change, and one
  // the author has to be told about rather than have quietly undone.
  c.reordered = !c.added.length && !c.removed.length && c.plan.tools.join() !== plan.tools.join()
  return c
}

/** Strip the floor back to what it cannot do without. */
export function clearTools(plan: FloorPlan): Change {
  const c = none(plan)
  c.removed = plan.tools.filter((t) => !REQUIRED.has(t))
  c.plan = { ...plan, tools: plan.tools.filter((t) => REQUIRED.has(t)) }
  return c
}

export function setPlanHall(plan: FloorPlan, hall: PlanHall): Change {
  const c = none(plan)
  if (plan.hall === hall) return c
  c.hallChanged = true
  c.plan = { ...plan, hall }
  return c
}

export function renamePlan(plan: FloorPlan, name: string): Change {
  const c = none(plan)
  const trimmed = name.trim().slice(0, 60)
  if (!trimmed || trimmed === plan.name) return c
  c.renamed = true
  c.plan = { ...plan, name: trimmed }
  return c
}

/** Put the aisle back in the order a wafer meets the tools. */
export function sortToFlow(plan: FloorPlan): Change {
  const c = none(plan)
  const sorted = inFlowOrder(plan.tools)
  if (sorted.join() === plan.tools.join()) return c
  c.reordered = true
  c.plan = { ...plan, tools: sorted }
  return c
}

/**
 * Move one tool along the aisle by one place - the dialog's up and down
 * arrows, and the only way to get a floor OUT of flow order on purpose.
 * A fab that grew by accretion has tools in the order it could afford them,
 * and walking one is a different lesson from walking a route.
 */
export function moveTool(plan: FloorPlan, id: string, delta: number): Change {
  const c = none(plan)
  const i = plan.tools.indexOf(id)
  const j = i + delta
  if (i < 0 || j < 0 || j >= plan.tools.length) return c
  const tools = [...plan.tools]
  ;[tools[i], tools[j]] = [tools[j], tools[i]]
  c.reordered = true
  c.plan = { ...plan, tools }
  return c
}
